"use client";
import React from "react";
import { Box, Button, Text, Input } from "@dxp-feed/ui";
import Link from "@/components/Link";
import { IListStores } from "@/utils/redux/storeLocator/index";
import ImageWrapper from "@/components/ImageWrapper";
import { sendSalesforceInteraction } from "@/helpers/cdnSdk";
import { addDataLayer } from "@/helpers/dataLayer";
import { getUTMParameter } from "@/helpers/getUTMParameters";
import { getStoreStatus } from "@/helpers/storeLocatorHelper";
import { useAppSelector } from "@/utils/hooks/reactUseCustomHooks";
import Loader from "@/components/Loader";

interface StoreSelectorProps {
  localSearchKey: string;
  searchKey: string;
  listStores: IListStores[];
  selectedStore: IListStores | null;
  setLocalSearchKey: React.Dispatch<React.SetStateAction<string>>;
  setSearchKey: React.Dispatch<React.SetStateAction<string>>;
  setConfirmationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleStoreSelection: (storeNumber: number) => void; // Update to number
  onHandleSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onItemClickHandler: () => void;
  searchLoading: boolean;
}

const StoreSelector: React.FC<StoreSelectorProps> = ({
  localSearchKey,
  searchKey,
  listStores,
  selectedStore,
  setLocalSearchKey,
  setSearchKey,
  handleStoreSelection,
  onHandleSearch,
  onItemClickHandler,
  searchLoading,
}) => {
  const userSession = useAppSelector((state) => state.gigyaslice);
  const { loggedIn: userLoggedIn, uid: Uid } = useAppSelector(
    (state) => state.gigyaslice
  );
  return (
    <Box className="custom-scroll md:max-h-[75svh] max-h-[87svh] !pr-0">
      <Box as="div" className="px-6">
        <Text
          as="h2"
          className="text-brandBlack font-semibold text-body sm:pt-4"
        >
          Prices shown on Sobeys.com reflect in-store prices for your selected
          store{" "}
        </Text>
        <Box
          as="div"
          className="shadow-[0_4px_8px_0px_rgba(0,0,0,0.25)] p-3 rounded-xl my-4 flex justify-between"
        >
          <Text as="p" className="text-brandBlack text-body">
            Shopping online for pickup or delivery?
            <Link
              href={`${process.env.NEXT_PUBLIC_VOILA_URL ?? ""}?utm_source=sobeyscorner&utm_medium=referral`}
              target="_blank"
              id="voila-link"
              aria-label={"Visit voila.ca"}
              className="text-brandBlack hover:text-brandSecondary font-semibold underline hover:no-underline text-body block"
              onClick={() => {
                addDataLayer({
                  event: "nav_header",
                  click_text: "Voila",
                  click_url: process.env.NEXT_PUBLIC_VOILA_URL || "",
                  page_title: document.title,
                });
                sendSalesforceInteraction({
                  name: "nav_click",
                  banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                  clickText: "Voila",
                  clickUrl: process.env.NEXT_PUBLIC_VOILA_URL || "",
                  screenClass: window?.location?.href,
                  screenName: document.title,
                  isAnonymous: userSession?.loggedIn ? 0 : 1,
                  gigyaUid: userSession?.uid || "",
                  utmSource: getUTMParameter("utm_source"),
                  utmMedium: getUTMParameter("utm_medium"),
                  utmCampaign: getUTMParameter("utm_campaign"),
                });
              }}
            >
              Visit voila.ca
            </Link>
          </Text>
          <ImageWrapper
            src="/logo/voila.svg"
            alt="voila"
            height={42}
            width={105}
          />
        </Box>
        <Box className="mt-3 mb-6 relative">
          <Box className="relative mb-2">
            <Input
              type="text"
              value={localSearchKey}
              onChange={onHandleSearch}
              className="w-full border-grey350 rounded py-0 h-10 px-4 pr-9 placeholder-grey900 text-brandBlack"
              placeholder="Search by city or postal code"
            />
            {searchKey ? (
              <button
                className="text-gray-500 hover:text-gray-800 absolute right-3 top-1/2 -translate-y-1/2"
                aria-label="Close"
                onClick={() => {
                  setSearchKey("");
                  setLocalSearchKey("");
                }}
              >
                ✕
              </button>
            ) : (
              <Text
                as="span"
                className="icon-search absolute right-3 top-1/2 -translate-y-1/2 text-brandBlack text-xl"
              />
            )}
          </Box>
          <Link
            href={`/store-locator`}
            className="text-brandSecondary font-semibold underline hover:no-underline text-body ml-1 inline"
            onClick={onItemClickHandler}
            aria-label={"View Store Locator"}
          >
            View Store Locator
          </Link>
        </Box>
      </Box>
      <ul>
        {searchLoading ? (
          <Loader className="h-[40px] w-[40px] mx-auto min-h-[400px]" />
        ) : listStores && listStores.length > 0 ? (
          listStores.slice(0, 3).map((item: IListStores) => (
            <li
              key={item.storeNumber}
              className={`${
                selectedStore?.storeNumber === item.storeNumber && "bg-grey100"
              } border-b-[1px] border-solid border-grey300 last:border-none`}
            >
              <Box as="div" className="py-3 px-6">
                {selectedStore?.storeNumber === item.storeNumber && (
                  <Text
                    as="span"
                    className="rounded-[4px] bg-brandSecondary py-[2px] px-2 text-body font-semibold mb-2 inline-block"
                  >
                    Selected Store
                  </Text>
                )}
                <Text
                  as="p"
                  className="text-brandBlack text-body font-bold mb-2"
                >
                  <span
                    className="text-brandBlack text-body font-bold mb-2"
                    role="link"
                    tabIndex={0}
                    aria-label={`${item.locationName}`}
                  >
                    {item.locationName}
                  </span>
                </Text>
                <Text as="p" className="text-brandBlack text-body mb-2">
                  <span
                    className="text-brandBlack text-body mb-2"
                    role="link"
                    tabIndex={0}
                    aria-label={`${[
                      item.address?.address1,
                      item.address?.address2,
                      item.address?.intersection,
                      item.address?.city,
                      item.address?.province,
                      item.address?.postalCode,
                    ]
                      .filter(Boolean)
                      .join(" ")}`}
                  >
                    {[
                      item.address?.address1,
                      item.address?.address2,
                      item.address?.intersection,
                      item.address?.city,
                      item.address?.province,
                      item.address?.postalCode,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </span>
                </Text>
                <Text as="p" className="text-brandBlack text-body mb-2">
                  <span
                    className="text-brandBlack text-body mb-2"
                    role="link"
                    tabIndex={0}
                    aria-label={`${
                      item?.hours?.is24Hours
                        ? "Open today"
                        : getStoreStatus(item?.hours?.storeHours)
                    }`}
                  >
                    {item?.hours?.is24Hours
                      ? "Open today"
                      : getStoreStatus(item?.hours?.storeHours)}
                  </span>
                </Text>
                <Box
                  as="div"
                  className="flex flex-wrap sm:gap-[10px] gap-4 overflow-auto justify-between items-center"
                >
                  <Link
                    href={`/stores/${item?.slug}`}
                    className="text-brandSecondary font-semibold underline hover:no-underline text-body"
                    onClick={onItemClickHandler}
                  >
                    View store details
                  </Link>
                  {item.storeNumber !== selectedStore?.storeNumber && (
                    <Button
                      className="hover:text-brandBlack font-normal"
                      aria-label="Select this stores"
                      // onClick={() => handleStoreSelection(item.storeNumber)}
                      onClick={() => {
                        handleStoreSelection(item.storeNumber);
                        addDataLayer({
                          event: "nav_header",
                          click_text: item?.locationName,
                          click_url: item.storeNumber.toString(),
                          page_title: document.title,
                        });
                        sendSalesforceInteraction({
                          name: "nav_click",
                          banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                          clickText: item?.locationName,
                          clickUrl: item.storeNumber.toString(),
                          screenClass: window?.location?.href,
                          screenName: document.title,
                          isAnonymous: Uid ? 0 : 1,
                          gigyaUid: Uid,
                          utmSource: getUTMParameter("utm_source"),
                          utmMedium: getUTMParameter("utm_medium"),
                          utmCampaign: getUTMParameter("utm_campaign"),
                        });
                      }}
                      // role="button"
                      variant="primary"
                      rounded="full"
                      type="button"
                    >
                      Select this store
                    </Button>
                  )}
                </Box>
              </Box>
            </li>
          ))
        ) : (
          <h3 className="flex items-center justify-center min-h-[400px] h-full py-4 text-center text-brandBlack font-semibold">
            No store found
          </h3>
        )}
      </ul>
    </Box>
  );
};

export default StoreSelector;
